<script lang="ts">
	import { isSupportedFile } from '#lib/pipeline';
	import { t } from '#lib/i18n/state.svelte';

	interface Props {
		onstart: (files: File[]) => void;
		disabled?: boolean;
		max?: number;
	}
	let { onstart, disabled = false, max = 20 }: Props = $props();

	const MAX_SIZE = 40 * 1024 * 1024;
	const ACCEPT = '.pdf,.docx,.tex,.latex,.ltx,.txt,.md,.markdown';

	let files = $state<File[]>([]);
	let error = $state<string | null>(null);
	let dragging = $state(false);
	let multiInput: HTMLInputElement;
	let folderInput: HTMLInputElement;
	let dragDepth = 0;

	function folderAttr(node: HTMLInputElement) {
		node.setAttribute('webkitdirectory', '');
		node.setAttribute('directory', '');
	}

	function addFiles(incoming: FileList | File[] | null) {
		if (!incoming || disabled) return;
		const list = Array.from(incoming);
		const next = [...files];
		let rejected = 0;
		let tooBig = 0;
		for (const file of list) {
			if (next.length >= max) {
				rejected++;
				continue;
			}
			if (!isSupportedFile(file)) {
				rejected++;
				continue;
			}
			if (file.size > MAX_SIZE || file.size === 0) {
				tooBig++;
				continue;
			}
			if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
			next.push(file);
		}
		files = next;
		if (rejected > 0) {
			error = t('batch.errorUnsupported', { n: rejected });
		} else if (tooBig > 0) {
			error = t('batch.errorSize', { n: tooBig });
		} else if (list.length + files.length > max) {
			error = t('batch.errorLimit', { n: max });
		} else {
			error = null;
		}
	}

	function onChange(e: Event) {
		const input = e.currentTarget as HTMLInputElement;
		addFiles(input.files);
		input.value = '';
	}

	function onDrop(e: DragEvent) {
		e.preventDefault();
		dragging = false;
		dragDepth = 0;
		if (disabled) return;
		addFiles(e.dataTransfer?.files ?? null);
	}

	function remove(index: number) {
		files = files.filter((_, i) => i !== index);
		error = null;
	}

	function start() {
		if (files.length === 0 || disabled) return;
		onstart(files);
	}
</script>

<section class="batch-upload">
	<div class="head">
		<p class="eyebrow">{t('batch.eyebrow')}</p>
		<h1>{t('batch.title')}</h1>
		<p class="lede">{t('batch.lede', { n: max })}</p>
	</div>

	<div
		class="dropzone"
		class:dragging
		role="button"
		tabindex="0"
		onclick={() => !disabled && multiInput?.click()}
		onkeydown={(e) => {
			if ((e.key === 'Enter' || e.key === ' ') && !disabled) multiInput?.click();
		}}
		ondragenter={(e) => {
			e.preventDefault();
			dragDepth++;
			dragging = true;
		}}
		ondragover={(e) => e.preventDefault()}
		ondragleave={() => {
			dragDepth--;
			if (dragDepth <= 0) dragging = false;
		}}
		ondrop={onDrop}
	>
		<span class="icon" aria-hidden="true">⇪</span>
		<p class="headline">{t('batch.drop')}</p>
		<p class="sub">{t('batch.or')}</p>
		<div class="pick-row">
			<button
				class="btn btn-sm"
				onclick={(e) => {
					e.stopPropagation();
					multiInput?.click();
				}}
				disabled={disabled}
			>
				{t('batch.chooseFiles')}
			</button>
			<button
				class="btn btn-sm"
				onclick={(e) => {
					e.stopPropagation();
					folderInput?.click();
				}}
				disabled={disabled}
			>
				{t('batch.chooseFolder')}
			</button>
		</div>
		<p class="limit">{t('batch.formats', { n: max })}</p>
	</div>

	<input bind:this={multiInput} type="file" multiple accept={ACCEPT} onchange={onChange} hidden />
	<input bind:this={folderInput} type="file" multiple accept={ACCEPT} onchange={onChange} use:folderAttr hidden />

	{#if error}<p class="error" role="alert">{error}</p>{/if}

	{#if files.length > 0}
		<ul class="selected">
			{#each files as file, i (file.name + file.size)}
				<li>
					<span class="fname">{file.name}</span>
					<span class="fsize">{t('batch.kb', { n: Math.max(1, Math.round(file.size / 1024)) })}</span>
					<button class="btn btn-ghost btn-sm" onclick={() => remove(i)} aria-label={t('batch.remove')}>✕</button>
				</li>
			{/each}
		</ul>
	{/if}

	<div class="actions">
		<span class="count">{t('batch.selected', { n: files.length, max })}</span>
		<button class="btn btn-primary" onclick={start} disabled={disabled || files.length === 0}>
			{t('batch.start', { n: files.length })}
		</button>
	</div>
</section>

<style>
	.batch-upload {
		border: 1px solid var(--line);
		border-radius: var(--radius);
		background: #fff;
		padding: clamp(1.2rem, 3vw, 2rem);
		box-shadow: var(--shadow-sm);
	}
	.head {
		max-width: 62ch;
	}
	.eyebrow {
		margin: 0 0 0.3rem;
		font-size: 0.7rem;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--muted);
		font-weight: 650;
	}
	h1 {
		font-size: 1.5rem;
		margin: 0 0 0.4rem;
	}
	.lede {
		margin: 0 0 1rem;
		color: var(--ink-2);
		font-size: 0.9rem;
	}
	.dropzone {
		border: 1.5px dashed var(--line-strong);
		border-radius: 16px;
		background: var(--paper-2);
		padding: 1.6rem 1rem;
		text-align: center;
		display: grid;
		justify-items: center;
		gap: 0.4rem;
		cursor: pointer;
	}
	.dropzone.dragging {
		border-color: var(--accent);
		background: #f0fdfa;
	}
	.icon {
		font-size: 1.6rem;
		color: var(--accent-ink);
	}
	.headline {
		margin: 0;
		font-weight: 600;
	}
	.sub {
		margin: 0;
		color: var(--muted);
		font-size: 0.85rem;
	}
	.pick-row {
		display: flex;
		gap: 0.4rem;
		flex-wrap: wrap;
		justify-content: center;
	}
	.limit {
		margin: 0.2rem 0 0;
		font-size: 0.74rem;
		color: var(--muted);
	}
	.error {
		color: var(--critical);
		background: var(--critical-soft);
		border: 1px solid #f2c9c4;
		border-radius: 9px;
		padding: 0.5rem 0.8rem;
		font-size: 0.84rem;
	}
	.selected {
		list-style: none;
		padding: 0;
		margin: 0.8rem 0 0;
		display: grid;
		gap: 0.3rem;
		max-height: 16rem;
		overflow: auto;
	}
	.selected li {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.35rem 0.5rem;
		border: 1px solid var(--line);
		border-radius: 8px;
		background: var(--paper);
	}
	.fname {
		flex: 1 1 auto;
		min-width: 0;
		overflow-wrap: anywhere;
		font-size: 0.84rem;
	}
	.fsize {
		font-size: 0.72rem;
		color: var(--muted);
		flex: none;
	}
	.actions {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.8rem;
		margin-top: 1rem;
		flex-wrap: wrap;
	}
	.count {
		font-size: 0.82rem;
		color: var(--muted);
	}
	@media (max-width: 560px) {
		.dropzone {
			padding: 1.2rem 0.8rem;
		}
		.actions .btn {
			width: 100%;
		}
	}
</style>
